/**
 * TaskMappingPanel Component
 *
 * Manage task type to session type mappings.
 * Maps event/task names to session types for automatic assignment.
 */

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTaskTypeMappings, useSessionTypes, useTimetableMutations } from '@/hooks/useTimetable'
import { Plus, Trash2, Link, Loader2 } from 'lucide-react'

export default function TaskMappingPanel() {
  const { mappings, loading, refetch } = useTaskTypeMappings()
  const { sessionTypes } = useSessionTypes()
  const { createMapping, deleteMapping } = useTimetableMutations()

  const [showForm, setShowForm] = useState(false)
  const [eventType, setEventType] = useState('')
  const [sessionTypeId, setSessionTypeId] = useState('')
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  const handleAddNew = () => {
    setEventType('')
    setSessionTypeId('')
    setError(null)
    setShowForm(true)
  }

  const handleSave = async () => {
    // Validation
    if (!eventType.trim()) {
      setError('Event type is required')
      return
    }
    if (!sessionTypeId) {
      setError('Session type is required')
      return
    }

    setSaving(true)
    setError(null)

    try {
      await createMapping({
        event_type: eventType.trim(),
        session_type_id: sessionTypeId
      })

      refetch()
      setShowForm(false)
      setEventType('')
      setSessionTypeId('')
    } catch (err) {
      console.error('[TaskMappingPanel] Save error:', err)
      setError(err.message || 'Failed to create mapping')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) {
      return
    }

    setDeletingId(mappingId)
    try {
      await deleteMapping(mappingId)
      refetch()
    } catch (err) {
      console.error('[TaskMappingPanel] Delete error:', err)
    } finally {
      setDeletingId(null)
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setEventType('')
    setSessionTypeId('')
    setError(null)
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="text-muted-foreground">Loading task mappings...</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            Task Type Mappings
          </CardTitle>
          {!showForm && (
            <Button variant="outline" size="sm" onClick={handleAddNew}>
              <Plus className="h-4 w-4 mr-2" />
              Add Mapping
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {/* Add new mapping form */}
        {showForm && (
          <Card className="mb-4 border-2 border-primary/20">
            <CardContent className="p-4">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="event-type">
                      Task/Event Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="event-type"
                      value={eventType}
                      onChange={(e) => setEventType(e.target.value)}
                      placeholder="e.g., Inoculation, Harvest, Shoogle..."
                      disabled={saving}
                    />
                    <p className="text-xs text-muted-foreground">
                      This will match task names containing this text
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mapping-session-type">
                      Session Type <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={sessionTypeId}
                      onValueChange={setSessionTypeId}
                      disabled={saving}
                    >
                      <SelectTrigger id="mapping-session-type">
                        <SelectValue placeholder="Select session type..." />
                      </SelectTrigger>
                      <SelectContent>
                        {sessionTypes.map(type => (
                          <SelectItem key={type.id} value={type.id}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded"
                                style={{ backgroundColor: type.colour || '#9CA3AF' }}
                              />
                              {type.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                    <p className="text-sm text-destructive">{error}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleCancel} disabled={saving}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Create Mapping
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mappings table */}
        {mappings.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No task mappings defined yet</p>
            <p className="text-sm mt-1">
              Add mappings to automatically assign tasks to session types
            </p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task/Event Name</TableHead>
                  <TableHead>Session Type</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappings.map(mapping => (
                  <TableRow key={mapping.id}>
                    <TableCell className="font-medium">
                      {mapping.event_type}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="border-none"
                        style={{
                          backgroundColor: mapping.session_type.colour + '20',
                          color: mapping.session_type.colour
                        }}
                      >
                        {mapping.session_type.name}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(mapping.id)}
                        disabled={deletingId === mapping.id}
                      >
                        {deletingId === mapping.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
